/**
 * API Client Utilities
 * Request retry logic and enhanced error handling
 */
import axios from 'axios';

/**
 * Retry failed HTTP requests with exponential backoff
 * 
 * @param {Function} requestFn - Function that returns axios promise
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} initialDelay - Initial delay in ms before first retry
 * @returns {Promise} - Axios response
 */
export const retryRequest = async (requestFn, maxRetries = 3, initialDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error;
            
            // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
            if (error.response) {
                const status = error.response.status;
                if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
                    throw error; // Don't retry client errors
                }
            }
            
            // If this is the last attempt, throw the error
            if (attempt === maxRetries) {
                break;
            }
            
            // Calculate delay with exponential backoff
            const delay = initialDelay * Math.pow(2, attempt);
            const jitter = Math.random() * 200; // Add random jitter to prevent thundering herd
            
            console.warn(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay + jitter}ms...`);
            
            await new Promise(resolve => setTimeout(resolve, delay + jitter));
        }
    }
    
    throw lastError;
};

/**
 * Format API error for user display
 * 
 * @param {Error} error - Axios error object
 * @returns {string} - User-friendly error message
 */
export const formatApiError = (error) => {
    if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        if (status === 429) {
            return '⏳ Too many requests. Please wait a moment and try again.';
        }
        
        if (status === 500) {
            return data?.message || '🔧 Server error. Our team has been notified. Please try again later.';
        }
        
        if (status === 404) {
            return '❌ Resource not found. Please check your request.';
        }
        
        if (status === 401) {
            return '🔒 Please log in to continue.';
        }
        
        if (status === 403) {
            return '🚫 You don\'t have permission to access this resource.';
        }
        
        // Try to extract message from response
        if (typeof data === 'object' && data.message) {
            return data.message;
        }
        
        if (typeof data === 'object' && data.detail) {
            // FastAPI detail format
            if (typeof data.detail === 'object' && data.detail.message) {
                return data.detail.message;
            }
            return data.detail;
        }
        
        return `Error ${status}: ${data || 'Something went wrong'}`;
    } else if (error.request) {
        // Request made but no response
        if (error.code === 'ECONNABORTED') {
            return '⏱️ Request timed out. Please check your internet connection and try again.';
        }
        return '📡 Unable to reach server. Please check your internet connection.';
    } else {
        // Request setup error
        return `⚠️ ${error.message || 'An unexpected error occurred'}`;
    }
};

export default {
    retryRequest,
    formatApiError
};
